import { useCallback } from 'react';

import type { Transaction } from 'prosemirror-state';

import {
	type VoiceDictationResult,
	VoiceDictationState,
	VoiceDictationStopReason,
} from '../effects/WebSpeechToTextModels';

import { useTranscriptionToEditorText } from './useTranscriptionToEditorText';

type EditorExecuteFn = (fn: (arg: { tr: Transaction }) => Transaction) => void;

export type EditorApiStub =
	| {
			core?: {
				actions?: {
					execute?: EditorExecuteFn;
				};
			};
	  }
	| undefined;

/**
 * Encapsulates the onVoiceDictationUpdate callback and the underlying
 * useTranscriptionToEditorText helpers. Intended to be shared between
 * chat-prompt-input and inline-chat-prompt consumers.
 */
export const useVoiceDictationEditorHandler = (
	editorApi: EditorApiStub,
): {
	onVoiceDictationUpdate: (result: VoiceDictationResult) => void;
	handleTranscriptUpdate: (transcript: string, tr: Transaction) => Transaction;
	rejectTranscriptedText: (tr: Transaction) => Transaction;
	disposeTranscriptedText: () => void;
} => {
	const { handleTranscriptUpdate, rejectTranscriptedText, disposeTranscriptedText } =
		useTranscriptionToEditorText();

	const onVoiceDictationUpdate = useCallback(
		(result: VoiceDictationResult): void => {
			switch (result.state) {
				case VoiceDictationState.Recording:
					if (!editorApi?.core?.actions?.execute) {
						return;
					}
					editorApi.core.actions.execute(({ tr }) =>
						handleTranscriptUpdate(result.transcription, tr),
					);
					break;
				case VoiceDictationState.Stopped:
					if (result.stopReason === VoiceDictationStopReason.TimeoutAccepted) {
						disposeTranscriptedText();
					}
					break;
				default:
					break;
			}
		},
		[handleTranscriptUpdate, editorApi, disposeTranscriptedText],
	);

	return {
		onVoiceDictationUpdate,
		handleTranscriptUpdate,
		rejectTranscriptedText,
		disposeTranscriptedText,
	};
};
